import { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Copy, ExternalLink, Loader2, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getAdminSpeechTopics } from '@/api/speechTopic';
import { CONTENT_CATEGORY_LABELS, type AdminSpeechTopic, type ContentCategory } from '@shared/api.interface';
import {
  buildListSharePath,
  buildListShareText,
  normalizeShareListTitle,
  buildShareCode,
  SHARE_LIST_MAX_TOPICS,
  SHARE_LIST_TITLE_MAX_LENGTH,
} from '@shared/share-link';
import { CATEGORY_STYLES, getCategoryStyle } from '@/utils/topic-category-style';
import { copyText } from '@/utils/share-topic';
import { extractApiErrorMessage } from './api-error';

const CATEGORY_ORDER = Object.keys(CATEGORY_STYLES) as ContentCategory[];

const chipClass = (on: boolean) =>
  `h-8 shrink-0 rounded-full px-3 text-xs transition-colors ${on ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent hover:text-foreground'}`;

function Thumb({ topic, className }: { topic: AdminSpeechTopic; className: string }) {
  const style = getCategoryStyle(topic.content_category);
  return topic.cover_image ? (
    <img src={topic.cover_image} alt="" loading="lazy" className={`aspect-video shrink-0 rounded-md object-cover ${className}`} />
  ) : (
    <span className={`aspect-video shrink-0 rounded-md ${className}`} style={{ background: style.coverBg }} />
  );
}

/** 勾选几个主题，生成一个清单链接发给家长（/list/编号,编号...） */
export function ShareListPanel() {
  const [allTopics, setAllTopics] = useState<AdminSpeechTopic[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<ContentCategory | 'all'>('all');
  const [picked, setPicked] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [link, setLink] = useState('');

  const fetchTopics = useCallback(async () => {
    setIsLoading(true);
    try {
      // 编号要在全部主题（含下架）里唯一，列表只显示上架的
      const result = await getAdminSpeechTopics({ status: 'all' });
      setAllTopics(result.items);
    } catch (error) {
      toast.error(extractApiErrorMessage(error, '获取主题失败'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchTopics();
  }, [fetchTopics]);

  const allIds = useMemo(() => allTopics.map((topic) => topic.id), [allTopics]);
  const activeTopics = useMemo(() => allTopics.filter((topic) => topic.is_active), [allTopics]);
  const byId = useMemo(() => new Map(allTopics.map((topic) => [topic.id, topic])), [allTopics]);
  const keyword = search.trim().toLowerCase();
  const visible = activeTopics.filter(
    (topic) => (category === 'all' || topic.content_category === category) && (!keyword || topic.title.toLowerCase().includes(keyword)),
  );
  const pickedTopics = picked.map((id) => byId.get(id)).filter((topic): topic is AdminSpeechTopic => Boolean(topic));

  const togglePick = (id: string) => {
    setLink('');
    setPicked((current) => {
      if (current.includes(id)) return current.filter((item) => item !== id);
      if (current.length >= SHARE_LIST_MAX_TOPICS) {
        toast.info(`一个清单最多 ${SHARE_LIST_MAX_TOPICS} 个主题`);
        return current;
      }
      return [...current, id];
    });
  };

  const generate = () => {
    const codes = pickedTopics.map((topic) => buildShareCode(topic.id, allIds));
    setLink(`${window.location.origin}${buildListSharePath(codes, title)}`);
  };

  // 复制的是“清单文案 + 链接”，和家长那边看到的分享文案一致
  const shareText = link
    ? buildListShareText(normalizeShareListTitle(title), pickedTopics.map((topic) => topic.title), link).full
    : '';

  const copyLink = async () => {
    if (await copyText(shareText)) toast.success('已复制，去微信粘贴发给家长');
    else toast.error('复制失败，请手动选中链接复制');
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">分享清单</h2>
        <p className="mt-1 text-sm text-muted-foreground">勾选几个主题，生成一个链接发给家长，对方打开就是这几个主题的清单</p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] lg:items-start">
          <section className="min-w-0 space-y-3 rounded-2xl border border-border bg-card p-4">
            <div>
              <h3 className="font-semibold">挑选主题</h3>
              <p className="text-xs text-muted-foreground">只列出上架的主题，最多选 {SHARE_LIST_MAX_TOPICS} 个</p>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="搜主题名"
                aria-label="搜主题名"
                className="pl-9"
              />
            </div>
            <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1">
              <button type="button" className={chipClass(category === 'all')} onClick={() => setCategory('all')}>
                全部
              </button>
              {CATEGORY_ORDER.map((key) => (
                <button key={key} type="button" className={chipClass(category === key)} onClick={() => setCategory(key)}>
                  {CONTENT_CATEGORY_LABELS[key]}
                </button>
              ))}
            </div>
            <div className="max-h-[60vh] space-y-1 overflow-y-auto pr-1">
              {visible.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">没有找到符合条件的主题</p>}
              {visible.map((topic) => {
                const on = picked.includes(topic.id);
                return (
                  <button
                    key={topic.id}
                    type="button"
                    role="checkbox"
                    aria-checked={on}
                    onClick={() => togglePick(topic.id)}
                    className={`flex w-full items-center gap-3 rounded-xl border-[1.5px] p-1.5 text-left transition-colors ${
                      on ? 'border-orange-200 bg-orange-50' : 'border-transparent hover:bg-accent'
                    }`}
                  >
                    <Thumb topic={topic} className="w-20" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold">{topic.title}</span>
                      <span className="block text-xs text-muted-foreground">
                        {CONTENT_CATEGORY_LABELS[topic.content_category]} · {topic.estimated_duration}
                      </span>
                    </span>
                    <span
                      className={`mr-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 ${
                        on ? 'border-primary bg-primary text-primary-foreground' : 'border-stone-300'
                      }`}
                    >
                      {on && <Check className="h-3.5 w-3.5" strokeWidth={3.5} />}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="min-w-0 space-y-3 rounded-2xl border border-border bg-card p-4 lg:sticky lg:top-4">
            <div>
              <h3 className="font-semibold">已选 {pickedTopics.length} 个</h3>
              <p className="text-xs text-muted-foreground">顺序就是清单里的顺序</p>
            </div>
            {pickedTopics.length === 0 ? (
              <p className="rounded-xl bg-muted/60 py-6 text-center text-sm text-muted-foreground">在左边点主题即可选中</p>
            ) : (
              <ol className="space-y-1.5">
                {pickedTopics.map((topic) => (
                  <li key={topic.id} className="flex items-center gap-2 rounded-lg bg-muted/50 px-2 py-1.5 text-sm">
                    <Thumb topic={topic} className="w-11" />
                    <span className="min-w-0 flex-1 truncate">{topic.title}</span>
                    <button
                      type="button"
                      aria-label={`移除${topic.title}`}
                      onClick={() => togglePick(topic.id)}
                      className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-accent"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ol>
            )}
            <div className="space-y-1">
              <label htmlFor="share-list-title" className="text-sm font-medium">
                清单标题（选填）
              </label>
              <Input
                id="share-list-title"
                value={title}
                maxLength={SHARE_LIST_TITLE_MAX_LENGTH}
                onChange={(event) => {
                  setTitle(event.target.value);
                  setLink('');
                }}
                placeholder={`为你挑选的 ${pickedTopics.length || 'N'} 个演讲主题`}
              />
            </div>
            <Button className="h-11 w-full" disabled={pickedTopics.length === 0} onClick={generate}>
              生成分享链接
            </Button>
            {link && (
              <div className="space-y-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                <p className="text-sm font-medium text-emerald-900">已生成，复制的内容如下</p>
                <p className="whitespace-pre-line break-all rounded-lg bg-white/70 p-2 text-xs leading-relaxed text-emerald-900">{shareText}</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" className="bg-white" onClick={() => void copyLink()}>
                    <Copy className="mr-1.5 h-4 w-4" />
                    复制文案和链接
                  </Button>
                  <Button variant="outline" className="bg-white" asChild>
                    <a href={link} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="mr-1.5 h-4 w-4" />
                      预览清单页
                    </a>
                  </Button>
                </div>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
